import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { CodeCategoryNode } from '@longjie/shared';
import { request } from './api';
import { subscribeCatalogChanges } from './catalog-change-sync';

type CategorySetting = { prefix: string; name: string; sortOrder: number };
type CategoryMetadata = { name: string | null; label: string; sortOrder: number | null };
export type CodeCategoryScope = 'all' | 'catalog';
const channelName = 'longjie-code-categories';
const storageKey = 'longjie-code-category-change';
const listeners = new Map<() => void, CodeCategoryScope>();
let snapshot = { metadata: new Map<string, CategoryMetadata>(), trees: new Map<CodeCategoryScope, CodeCategoryNode[]>() };
const revisions = new Map<string, number>();
let interval: ReturnType<typeof setInterval> | undefined;
let scheduled: ReturnType<typeof setTimeout> | undefined;
let channel: BroadcastChannel | undefined;
let unsubscribeCatalogChanges: (() => void) | undefined;
const controllers = new Map<CodeCategoryScope, AbortController>();

function notify() { listeners.forEach((_scope, listener) => listener()); }

function sameMetadata(a: CategoryMetadata | undefined, b: CategoryMetadata) {
  return a?.name === b.name && a?.label === b.label && a?.sortOrder === b.sortOrder;
}

function applySetting(setting: CategorySetting) {
  revisions.set(setting.prefix, (revisions.get(setting.prefix) ?? 0) + 1);
  const latest = {
    name: setting.name, label: `${setting.prefix} · ${setting.name}`, sortOrder: setting.sortOrder,
  };
  if (sameMetadata(snapshot.metadata.get(setting.prefix), latest)) return;
  snapshot = { ...snapshot, metadata: new Map(snapshot.metadata).set(setting.prefix, latest) };
  notify();
}

function receive(value: unknown) {
  if (!value || typeof value !== 'object') return;
  const setting = value as CategorySetting;
  if (typeof setting.prefix !== 'string' || !/^(?:[0-9]{2}){1,24}$/.test(setting.prefix)
    || typeof setting.name !== 'string' || !setting.name.trim() || setting.name.length > 80
    || !Number.isInteger(setting.sortOrder) || setting.sortOrder < 0 || setting.sortOrder > 99_999) return;
  applySetting(setting);
}

function onStorage(event: StorageEvent) {
  if (event.key !== storageKey || !event.newValue) return;
  try { receive(JSON.parse(event.newValue)); } catch { /* Ignore unrelated malformed storage events. */ }
}

async function refreshScope(scope: CodeCategoryScope) {
  if (controllers.has(scope) || document.visibilityState === 'hidden') return;
  const current = new AbortController();
  const before = new Map(revisions);
  controllers.set(scope, current);
  try {
    const result = await request<{ codeCategories: CodeCategoryNode[] }>(`/api/v1/code-categories?scope=${scope}`, { signal: current.signal });
    if (current.signal.aborted || !Array.isArray(result.codeCategories)) return;
    const next = new Map(snapshot.metadata);
    let changed = false;
    for (const node of result.codeCategories) {
      // A saved name must survive a read that began before that save completed.
      if ((before.get(node.prefix) ?? 0) !== (revisions.get(node.prefix) ?? 0)) continue;
      const latest = { name: node.name ?? null, label: node.label, sortOrder: node.sortOrder ?? null };
      if (!sameMetadata(next.get(node.prefix), latest)) { next.set(node.prefix, latest); changed = true; }
    }
    const treeChanged = JSON.stringify(snapshot.trees.get(scope)) !== JSON.stringify(result.codeCategories);
    if (changed || treeChanged) {
      snapshot = { metadata: next, trees: new Map(snapshot.trees).set(scope, result.codeCategories) };
      notify();
    }
  } catch { /* Keep saved names through temporary network/auth failures. */ }
  finally { if (controllers.get(scope) === current) controllers.delete(scope); }
}

function refreshVisible() {
  for (const scope of new Set(listeners.values())) void refreshScope(scope);
}

function scheduleRefresh() {
  if (scheduled !== undefined) clearTimeout(scheduled);
  scheduled = setTimeout(() => { scheduled = undefined; refreshVisible(); }, 100);
}

function subscribe(listener: () => void, scope: CodeCategoryScope) {
  listeners.set(listener, scope);
  if (interval === undefined) {
    unsubscribeCatalogChanges = subscribeCatalogChanges(() => {
      for (const controller of controllers.values()) controller.abort();
      controllers.clear();
      scheduleRefresh();
    });
    interval = setInterval(refreshVisible, 30_000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    window.addEventListener('storage', onStorage);
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(channelName);
      channel.onmessage = (event) => receive(event.data);
    }
  }
  scheduleRefresh();
  return () => {
    listeners.delete(listener);
    if (![...listeners.values()].includes(scope)) {
      controllers.get(scope)?.abort();
      controllers.delete(scope);
    }
    if (listeners.size) return;
    clearInterval(interval);
    clearTimeout(scheduled);
    interval = undefined;
    scheduled = undefined;
    channel?.close();
    channel = undefined;
    unsubscribeCatalogChanges?.();
    unsubscribeCatalogChanges = undefined;
    window.removeEventListener('focus', refreshVisible);
    document.removeEventListener('visibilitychange', refreshVisible);
    window.removeEventListener('storage', onStorage);
    snapshot = { metadata: new Map(), trees: new Map() };
    revisions.clear();
  };
}

function getSnapshot() { return snapshot; }

/** Call only after the server confirms a manual category name/order save. */
export function publishCodeCategorySetting(setting: CategorySetting) {
  if (listeners.size) applySetting(setting);
  const publisher = channel ?? (typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(channelName) : undefined);
  publisher?.postMessage(setting);
  if (publisher !== channel) publisher?.close();
  try { localStorage.setItem(storageKey, JSON.stringify({ ...setting, nonce: crypto.randomUUID() })); }
  catch { /* BroadcastChannel and polling also cover storage-disabled browsers. */ }
}

/** Share complete trees within a visibility scope and manual names across every scope. */
export function useSynchronizedCodeCategories<T extends CodeCategoryNode[] | undefined>(nodes: T, scope: CodeCategoryScope = 'all'): T {
  const enabled = nodes !== undefined;
  const subscribeVisible = useCallback((listener: () => void) => enabled ? subscribe(listener, scope) : () => {}, [enabled, scope]);
  const current = useSyncExternalStore(subscribeVisible, getSnapshot, getSnapshot);
  useEffect(() => {
    if (!nodes) return;
    const synchronized = getSnapshot().trees.get(scope);
    // A page's explicit reload can discover changes before the next poll. Recheck
    // differing data without allowing an older page response to replace saved names.
    if (synchronized && JSON.stringify(synchronized) !== JSON.stringify(nodes)) {
      controllers.get(scope)?.abort();
      controllers.delete(scope);
      scheduleRefresh();
    }
  }, [nodes, scope]);
  return useMemo(() => {
    if (!nodes) return nodes;
    const source = current.trees.get(scope) ?? nodes;
    if (!current.metadata.size) return source as T;
    const children = new Map<string | null, CodeCategoryNode[]>();
    for (const original of source) {
      const latest = current.metadata.get(original.prefix);
      const node = { ...original };
      if (latest) {
        node.label = latest.label;
        if (latest.name === null) delete node.name; else node.name = latest.name;
        if (latest.sortOrder === null) delete node.sortOrder; else node.sortOrder = latest.sortOrder;
      }
      const siblings = children.get(node.parentPrefix) ?? [];
      siblings.push(node);
      children.set(node.parentPrefix, siblings);
    }
    for (const siblings of children.values()) siblings.sort((a, b) =>
      (a.sortOrder ?? Number(a.segment) * 10) - (b.sortOrder ?? Number(b.segment) * 10) || a.prefix.localeCompare(b.prefix, 'en'));
    const ordered: CodeCategoryNode[] = [];
    function visit(parent: string | null) {
      for (const node of children.get(parent) ?? []) { ordered.push(node); visit(node.prefix); }
    }
    visit(null);
    return ordered as T;
  }, [nodes, current, scope]);
}
